import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Paper, Stack, TextField, Button } from '@mui/material';
import { ArrowForwardIos, RestartAlt } from '@mui/icons-material';
import Notification from '../Notification';
import { useNavigate } from 'react-router-dom';

const initialValues = {
  stipend_amount: '',
  effective_from_date: '',
};

function EmployeeStipendForm({ employeeId, initialData, onSuccess }) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const navigate = useNavigate();

  useEffect(() => {
    if (initialData) {
      setValues({
        stipend_amount: initialData.stipend_amount?.toString() || '',
        effective_from_date: initialData.effective_from_date || '',
      });
    } else {
      setValues(initialValues);
    }
  }, [initialData]);

  const validate = () => {
    const temp = {};
    temp.stipend_amount = values.stipend_amount ? '' : 'Stipend amount is required.';
    temp.effective_from_date = values.effective_from_date ? '' : 'Effective from date is required.';
    setErrors(temp);
    return Object.values(temp).every((x) => x === '');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === 'stipend_amount') {
      newValue = value.replace(/[^\d.]/g, '');
    }
    setValues((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const resetForm = () => {
    if (initialData) {
      setValues({
        stipend_amount: initialData.stipend_amount?.toString() || '',
        effective_from_date: initialData.effective_from_date || '',
      });
    } else {
      setValues(initialValues);
    }
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      employee_id: employeeId,
      stipend_amount: Number(values.stipend_amount),
      effective_from_date: values.effective_from_date,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/apprentice-master/add-or-update-stipend`,
        payload
      );
      if (response.status === 200) {
        setNotify({
          isOpen: true,
          message:
            response.data?.message || 'Stipend added/updated successfully',
          type: 'success',
        });
        if (onSuccess) onSuccess();
      }
    } catch (e) {
      if (e.response && e.response.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error',
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setNotify({
          isOpen: true,
          message: e.response?.data?.message || 'Failed to save stipend',
          type: 'error',
        });
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        margin: '-15px',
        padding: '30px',
        fontWeight: '500px',
      }}
    >
      <Paper elevation={3} sx={{ width: '500px', marginTop: '-10px' }}>
        <form autoComplete="off" onSubmit={handleSubmit}>
          <Stack spacing={3} sx={{ margin: '30px' }}>
            <TextField
              variant="outlined"
              name="stipend_amount"
              label="Stipend Amount"
              size="small"
              value={values.stipend_amount}
              onChange={handleInputChange}
              {...(errors.stipend_amount && {
                error: true,
                helperText: errors.stipend_amount,
              })}
            />

            <TextField
              variant="outlined"
              type="date"
              name="effective_from_date"
              label="Effective From Date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={values.effective_from_date}
              onChange={handleInputChange}
              {...(errors.effective_from_date && {
                error: true,
                helperText: errors.effective_from_date,
              })}
            />

            <Stack direction="row" justifyContent="space-between">
              <Button
                className="form-btn"
                variant="contained"
                color="error"
                onClick={resetForm}
                endIcon={<RestartAlt />}
              >
                Reset
              </Button>
              <Button
                className="form-btn"
                variant="contained"
                type="submit"
                endIcon={<ArrowForwardIos sx={{ width: 18, height: 18 }} />}
              >
                Submit
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
      <Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
}

export default EmployeeStipendForm;

